
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { TrendingUp, DollarSign, Target, AlertTriangle } from "lucide-react";
import { formatCurrency, calculatePriceToCustomer, calculateAverageCostPerTask, calculateAdjustedBufferAmount, calculateTotalWithAdjustedBuffer, calculateOverallBlendedRate } from "./utils/costCalculations";

interface ProjectCostAnalysis {
  total_cost: number;
}

interface MarginProjectionsSectionProps {
  costAnalysis: ProjectCostAnalysis | null;
  selectedProject: any;
  profitMargin: number[];
  setProfitMargin: (value: number[]) => void;
  bufferPercent: number[];
  setBufferPercent: (value: number[]) => void;
  taskProfitability: any[];
  calculatedTotalCost?: number;
  resourceAssignments?: any[];
}

const MarginProjectionsSection = ({
  costAnalysis,
  selectedProject,
  profitMargin,
  setProfitMargin,
  bufferPercent,
  setBufferPercent,
  taskProfitability,
  calculatedTotalCost,
  resourceAssignments = []
}: MarginProjectionsSectionProps) => {
  // Use the calculated total cost if available, otherwise fall back to costAnalysis
  const actualTotalCost = calculatedTotalCost || costAnalysis?.total_cost || 0;

  console.log('📊 MARGIN PROJECTIONS: Using total cost:', {
    calculatedTotalCost,
    costAnalysisTotalCost: costAnalysis?.total_cost || 0,
    actualTotalCostUsed: actualTotalCost,
    resourceAssignmentsCount: resourceAssignments.length
  });

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-primary h-[380px]">
      <CardHeader className="pb-2">
        <CardTitle className="text-white text-lg flex items-center gap-2">
          Overall Project Margin Projections
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <span className="text-[18px] font-bold text-primary">Projected Price to Customer: </span>
              <span className="text-[18px] text-green-300 font-bold">
                {formatCurrency(calculatePriceToCustomer(actualTotalCost, profitMargin[0], bufferPercent[0]))}
              </span>
            </div>
            <div className="text-right">
              <span className="text-[16px] font-bold text-primary">Overall Blended Rate: </span>
              <span className="text-[16px] text-blue-300 font-bold">
                {formatCurrency(calculateOverallBlendedRate(resourceAssignments, profitMargin[0], bufferPercent[0]))}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-300">
              <span>Profit Margin: {profitMargin[0]}%</span>
            </div>
            <Slider
              value={profitMargin}
              onValueChange={setProfitMargin}
              max={100}
              min={0}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>0%</span>
              <span>100%</span>
            </div>
          </div>
        </div>

        {/* Cost Overview Cards Below Slider */}
        <div className="grid grid-cols-2 gap-2 h-[190px]">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30 border rounded-lg p-2">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-blue-500/20 rounded">
                <DollarSign className="h-4 w-4 text-blue-300" />
              </div>
              <div>
                <p className="text-blue-200 text-[13px]">Average Cost Per Project Task</p>
                <p className="text-white text-[18px] font-semibold">{formatCurrency(calculateAverageCostPerTask(actualTotalCost, selectedProject?.tasks?.length || 0))}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/30 border rounded-lg p-2">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-green-500/20 rounded">
                <Target className="h-4 w-4 text-green-300" />
              </div>
              <div>
                <p className="text-green-200 text-[13px]">Total Project Cost</p>
                <p className="text-white text-[18px] font-semibold">{formatCurrency(actualTotalCost)}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border-purple-500/30 border rounded-lg p-2">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-purple-500/20 rounded">
                <AlertTriangle className="h-4 w-4 text-purple-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-purple-200 text-[13px]">Buffer Amount</p>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-purple-300">{bufferPercent[0]}%</span>
                  </div>
                </div>
                <p className="text-white text-[18px] font-semibold badge">{formatCurrency(calculateAdjustedBufferAmount(actualTotalCost, bufferPercent[0]))}</p>
                <div className="mt-3">
                  <Slider
                    value={bufferPercent}
                    onValueChange={setBufferPercent}
                    max={50}
                    min={0}
                    step={0.1}
                    className="w-full h-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-500/30 border rounded-lg p-2">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-orange-500/20 rounded">
                <TrendingUp className="h-4 w-4 text-orange-300" />
              </div>
              <div>
                <p className="text-orange-200 text-[13px]">Total Project Cost (With Buffer)</p>
                <p className="text-white text-[18px] font-semibold">{formatCurrency(calculateTotalWithAdjustedBuffer(actualTotalCost, bufferPercent[0]))}</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MarginProjectionsSection;
